import Color from 'color';
import { merge } from 'lodash';
import { colorWithLightness, colorWithOpacity, colorWithoutOpacity } from '../../../common/util/colors';
import { darkVhost } from './darkTheme';
import { MERGED_VHOST } from '../../../common/consts/VHost';

export const shades = {
	grey0: '#ffffff',
	grey10: '#f5f5f5',
	grey20: '#e6e6e6',
	grey30: '#c5c5c5',
	grey40: '#98989b',
	grey50: '#6e6e6e',
	grey60: '#3a3b3d',
	grey70: '#333333',
};

export const spacings = {
	distance2: '2px',
	distance4: '4px',
	distance6: '6px',
	distance8: '8px',
	distance10: '10px',
	distance12: '12px',
	distance16: '16px',
	distance20: '20px',
	distance24: '24px',
	distance32: '32px',
	distance40: '40px',
	distance64: '64px',
};

export const textSpacings = {
	spacingSM: spacings.distance4,
	spacingMD: spacings.distance4,
};

// TODO: The values are open for debate. Uncomment and extend this object with zIndexes that fit the needs within the Plek design system
export const zIndexes = {
	offScreen: -9999, // unsed?
	topFixedMenu: 1030, // top navigation header menu
	fixedOverlay: 1031, // for elements that show over the top menu
	pdfViewerToolbar: 1033, // unused?
	tooltip: 2000,
	overlayStartZIndex: 3000, // for OvelayModals - this value increses by 1 for every modal you add on top
	froala: 4000, // Custom z-index to make all toolbars and popups for Froala instances on top of surrounding DOM elements
	absoluteFront: 9999,
};

export const borderRadius = {
	XS: spacings.distance2,
	SM: spacings.distance4,
	MD: spacings.distance6,
	LG: spacings.distance8,
	XL: spacings.distance12,
	ROUND: '50%',
	btnFloating: spacings.distance32,
};

export const breakpointValue = {
	xs: '576px',
	sm: '768px',
	md: '992px',
	lg: '1200px',
	xl: '1500px',
};

// Text styles used by design https://www.figma.com/file/OTL9B402W1kPEZExvFr6MO/Plek-Desktop-design-system?node-id=1594-2258&t=rGpdh4AW8WI901hj-4
export const text = {
	h1: {
		fontSize: 32,
		lineHeight: 40,
		fontWeight: 400,
		fontFamily: 'Open Sans',
		fontStyle: 'normal',
	},
	h2: {
		fontSize: 24,
		lineHeight: 32,
		fontWeight: 600,
		fontFamily: 'Open Sans',
		fontStyle: 'normal',
	},
	h3: {
		fontSize: 20,
		lineHeight: 28,
		fontWeight: 600, // 400 is also used in design system
		fontFamily: 'Open Sans',
		fontStyle: 'normal',
	},
	h4: {
		fontSize: 18,
		lineHeight: 24,
		fontWeight: 600,
		fontFamily: 'Open Sans',
		fontStyle: 'normal',
	},
	h5: {
		fontSize: 16,
		lineHeight: 20,
		fontWeight: 600,
		fontFamily: 'Open Sans',
		fontStyle: 'normal',
	},
	h6: {
		fontSize: 14,
		lineHeight: 18,
		fontWeight: 600,
		fontFamily: 'Open Sans',
		fontStyle: 'normal',
	},
	body: {
		fontSize: 14,
		lineHeight: 20,
		fontWeight: 400,
		fontFamily: 'Open Sans',
		fontStyle: 'normal',
	},
	small: {
		fontSize: 12,
		lineHeight: 16,
		fontWeight: 400, // 600 is also used in design system
		fontFamily: 'Open Sans',
		fontStyle: 'normal', // italic is also used in design system
	},
};

// TODO: add deviceBreakpoint for portrait if needed in future, use as: @media (orientation: portrait) and ${deviceBreakpoint.xs.portrait} {}
export const deviceBreakpoint = {
	xs: {
		portrait: ``,
		landscape: `(min-width: ${breakpointValue.xs})`,
	},
	sm: {
		portrait: ``,
		landscape: `(min-width: ${breakpointValue.sm})`,
	},
	md: {
		portrait: ``,
		landscape: `(min-width: ${breakpointValue.md})`,
	},
	lg: {
		portrait: ``,
		landscape: `(min-width: ${breakpointValue.lg})`,
	},
	xl: {
		portrait: ``,
		landscape: `(min-width: ${breakpointValue.xl})`,
	},
};

// TODO: deprecate
export const sentensor = {
	blue: '#4F92E1',
	orange: '#EAA113',
	red: '#B93309',
	darkblue: '#002D39',
	yellow: '#FFF5E1',
	green: '#8DAB36',
};

export const shadows = {
	z1: ['0 0 1px rgba(0, 0, 0, 0.05)', '0 1px 2px rgba(0, 0, 0, 0.05)', '0 2px 6px rgba(0, 0, 0, 0.075)'],
	z2: ['0 0 1px rgba(0, 0, 0, 0.05)', '0 1px 2px rgba(0, 0, 0, 0.1)', '0 4px 6px rgba(0, 0, 0, 0.075)', '0 6px 12px rgba(0, 0, 0, 0.05)'],
	z3: ['0 0 1px rgba(0, 0, 0, 0.05)', '0 2px 4px rgba(0, 0, 0, 0.1)', '0 8px 12px rgba(0, 0, 0, 0.133)', '0 12px 24px rgba(0, 0, 0, 0.1)'],
};
export const fallbackVhost = '#008B99';
export const defaultVhost = MERGED_VHOST?.themecolor || fallbackVhost;
const loginTheme = MERGED_VHOST?.theme?.background?.login;

// Accessible colors are calculated with https://webaim.org/resources/contrastchecker
const defaultTheme = (theme) => {
	const vhost = theme?.colors?.vhost === darkVhost ? defaultVhost : theme?.colors?.vhost || defaultVhost;
	const vhostObj = Color(vhost).hsl() as any;
	const colors = {
		vhost,
		green: '#73b837',
		yellow: '#fab900',
		amber: '#fab65c',
		orange: '#e77335',
		red: '#c24236',
		// TODO: deprecate
		darkRed: '#B63827',
		darkblue: '#002D39',
		lightblue: '#E3F4F6',
		turquoise: '#006873',
		// Chat colors
		// TODO: deprecate
		chatSender: vhost,
		chatReceiver: colorWithoutOpacity(vhostObj.alpha(0.1 + vhostObj.color[2] * 0.002).string(), '#ffffff'),
		chatSenderEmbed: Color(vhost).darken(0.2).string(),
		chatReceiverEmbed: colorWithoutOpacity(vhostObj.alpha(0.18 + vhostObj.color[2] * 0.003).string(), '#ffffff'),
		// Form colors
		// TODO: deprecate
		formBubble: colorWithoutOpacity(vhostObj.alpha(0.1 + vhostObj.color[2] * 0.002).string(), '#ffffff'),
		formBubbleText: shades.grey70,
		// Generic add item colors
		// TODO: deprecate
		addItem: colorWithoutOpacity(vhostObj.alpha(0.07 + vhostObj.color[2] * 0.00015).string(), '#ffffff'),
	};
	const vhostShades = {
		vhost10: colorWithoutOpacity(colorWithOpacity(vhost, 0.07), '#ffffff'),
		vhost20: colorWithoutOpacity(colorWithOpacity(vhost, 0.2), '#ffffff'),
		vhost30: colorWithoutOpacity(colorWithOpacity(vhost, 0.5), '#ffffff'),
		vhost40: vhost,
		vhost50: colorWithLightness(vhost, 0.75),
		vhost60: colorWithLightness(vhost, 0.5),
		vhost70: colorWithLightness(vhost, 0.25),
	};

	return calculateForegroundColors(
		merge({}, theme, {
			name: 'default',
			// For larger text and backgrounds
			colors,
			// For small text
			accessibleColors: {
				...colors,
				green: '#4e8022',
				yellow: '#bd8a00',
				amber: '#bd6b00',
				orange: '#c45417',
				red: '#b52d2d',
			},
			backgroundColors: {
				primary: '#ffffff',
				secondary: '#f0f1f2',
				// Chat colors
				chat: colorWithoutOpacity(vhostObj.alpha(0.032 + vhostObj.color[2] * 0.00032).string(), '#ffffff'),
				red: 'rgba(219, 74, 55, 0.1)',
				avatar: colorWithoutOpacity(vhostObj.alpha(0.2 + vhostObj.color[2] * 0.004).string(), '#ffffff'),
			},
			textColors: {
				body: shades.grey70,
				light: shades.grey60,
				lighter: shades.grey50,
				lightest: shades.grey40,
			},
			outlineColor: '#d6d6d6',
			inputColor: colorWithoutOpacity(colorWithOpacity(vhostShades.vhost20, 0.3), shades.grey20),
			shades,
			vhostShades,
			spacings,
			breakpointValue,
			borderRadius,
			text,
			zIndexes,
			elevation: {
				// Container
				// e.g. (Quick) post, timeline items
				z1: {
					backgroundOverlayColor: '#ffffff',
					backgroundOverlay: 'linear-gradient(#ffffff, #ffffff)',
					boxShadow: shadows.z1.join(', '),
					dropShadow: shadows.z1.map((shadow) => `drop-shadow(${shadow})`).join(' '),
					insetBoxShadow: 'none',
				},
				// Non blocking
				// e.g. tooltip, dropdown
				z2: {
					backgroundOverlayColor: '#ffffff',
					backgroundOverlay: 'linear-gradient(#ffffff, #ffffff)',
					boxShadow: shadows.z2.join(', '),
					dropShadow: shadows.z2.map((shadow) => `drop-shadow(${shadow})`).join(' '),
					insetBoxShadow: 'none',
				},
				// Blocking
				// e.g. dialog
				z3: {
					backgroundOverlayColor: '#ffffff',
					backgroundOverlay: 'linear-gradient(#ffffff, #ffffff)',
					boxShadow: shadows.z3.join(', '),
					dropShadow: shadows.z3.map((shadow) => `drop-shadow(${shadow})`).join(' '),
					insetBoxShadow: 'none',
				},
			},
			scrollbar: {
				thumb: shades.grey50,
				thumbHover: shades.grey40,
				thumbActive: shades.grey30,
				trackBg: shades.grey0,
			},
			login: {
				backgroundImage: loginTheme?.path,
				backgroundColor: loginTheme?.backgroundColor,
			},
		})
	);
};

// Calculate and add foreground colors (black or white) to be used for text and icons on top of colors
export const calculateForegroundColors = (theme) =>
	merge({}, theme, {
		foregroundColors: Object['fromEntries'](
			Object.entries(theme.colors).map(([key, color]) => {
				const colorObj = Color(color as string).hsl();
				const isLight = Color('#000000').contrast(colorObj) - 10 > Color('#ffffff').contrast(colorObj);
				return [key, isLight ? '#000000' : '#ffffff'];
			})
		),
	});

export default defaultTheme;
